import { Button, Flex, Popconfirm, Space, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useFetcher } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import {
  CustomTable,
  CustomTextField,
  ReservationStatusTag,
  TableHeaderBar,
  TableHeaderInfo,
} from "~/components";
import { Reservation, ReservationListFiltersType } from "~/types";
import { dateFormat, INTENTS, simpleDateFormat } from "~/utils";
import { useState } from "react";
import { TableRowSelection } from "antd/es/table/interface";
import { ReservationListFilters } from "../ReservationTableFilter";
import dayjs from "dayjs";
import * as APITypes from "~/API";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import { TFunction } from "i18next";

export const getReservationTableColumns = (
  t: TFunction
): ColumnsType<Reservation> => [
    {
      title: t("Request_Created"),
      render: (_, record: Reservation) => {
        return (
          <CustomTextField
            value={dateFormat(
              dayjs(record.data.createdAt),
              "D MMM YYYY; h:mm:ss.SSS a"
            )}
          />
        );
      },
      sorter: (a, b) => a.createdAt.diff(b.createdAt),
      key: "created",
      width: 200,
    },
    {
      title: t("Start_Date"),
      render: (_, record: Reservation) => {
        return (
          <CustomTextField value={simpleDateFormat(record.timeFrame.start)} />
        );
      },
      sorter: (a, b) => a.timeFrame.start.diff(b.timeFrame.start),
      key: "start",
      width: 200,
    },
    {
      title: t("End_Date"),
      render: (_, record: Reservation) => {
        return (
          <CustomTextField value={simpleDateFormat(record.timeFrame.end)} />
        );
      },
      sorter: (a, b) => a.timeFrame.end.diff(b.timeFrame.end),
      key: "end",
      width: 200,
    },
    {
      title: t("Status"),
      render: (_, record: Reservation) => {
        return <ReservationStatusTag status={record.data.status} />;
      },
      key: "status",
      width: 150,
    },
    {
      title: t("Details"),
      key: "details",
      width: 250,
      render: (_, record: Reservation) => {
        return <CustomTextField value={record.data.details} />;
      },
    },
    {
      title: t("Party_size"),
      key: "partySize",
      width: 250,
      render: (_, record: Reservation) => {
        return <CustomTextField value={record.data.partySize} />;
      },
    },
    {
      title: t("Reason"),
      key: "reason",
      width: 250,
      render: (_, record: Reservation) => {
        return <CustomTextField value={record.data.statusReason} />;
      },
    },
  ];

export const ReservationTable = ({
  reservations,
  filters,
}: {
  reservations: Reservation[];
  filters: ReservationListFiltersType;
}) => {
  const { t } = useAppTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const fetcher = useFetcher();
  const { isTableLoading } = useRouteActivity();
  const isMutating = fetcher.state !== "idle";

  const toggleConfirmCancel = () => setShowConfirmCancel(!showConfirmCancel);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<Reservation> = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled:
        record.data.status === APITypes.AmenityReservationStatus.Rejected ||
        record.data.status === APITypes.AmenityReservationStatus.Canceled,
    }),
  };

  const handleModifyReservations = (
    reservationIDs: string[],
    intent: string
  ) => {
    fetcher.submit(
      {
        body: JSON.stringify({
          reservationIDs,
        }),
        intent,
      },
      { method: "POST" }
    );
  };

  const hasSelected = selectedRowKeys.length > 0;

  const filtered = reservations.filter((value) => {
    if (!filters.status) return true;
    return filters.status === value.data.status;
  });

  return (
    <>
      <section style={{ marginTop: "2rem" }}>
        <CustomTable
          tableHeaders={
            <>
              <TableHeaderBar
                left={
                  <TableHeaderInfo
                    title={t("Your_Reservations")}
                    count={filtered.length}
                    countLabel={t("Total_Records_Found")}
                  />
                }
              />
              <ReservationListFilters filters={filters} />
            </>
          }
          mobileCardConfig={{ titleKey: "status", primaryKeys: ["startDate"] }}
          tableProps={{
            dataSource: filtered.map((value, index) => {
              return {
                key: index,
                ...value,
              };
            }),
            columns: getReservationTableColumns(t),
            rowSelection: rowSelection,
            loading: isTableLoading,
          }}
          footer={
            <Flex gap={5}>
              <Popconfirm
                title={
                  <>
                    <b>Cancel selected reservations?</b>
                    <br />
                    This action cannot be undone.
                  </>
                }
                open={showConfirmCancel}
                // Using "cancel" here may be unintuitive
                cancelText={"Abort"}
                onCancel={toggleConfirmCancel}
                onConfirm={() => {
                  handleModifyReservations(
                    selectedRowKeys.map(
                      (value) => filtered[value as number].data.id
                    ),
                    INTENTS._edit_amenity_reservations_cancel
                  );
                  setSelectedRowKeys([]);
                  setShowConfirmCancel(false);
                }}
                okText={"Confirm"}
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="primary"
                  disabled={!hasSelected || isMutating}
                  loading={isMutating}
                  danger
                  onClick={toggleConfirmCancel}
                >
                  Cancel Reservations
                </Button>
              </Popconfirm>
            </Flex>
          }
        />
      </section>
    </>
  );
};
